import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import { usePropertyDeckardId } from '@/utils/hooks/usePropertyDeckardId';
import dayjs from 'dayjs';
import { TaxConfiguration } from 'deckard-ui/dist/components/TaxRemittanceViewer/types';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RentalscapeConfig } from '../api/ariadne/__generated_types__/apollo';
import { BETA_FEATURES, PROPERTY_DETAILS_TAB_NAMES } from '../constants';
import * as dashboardActions from '../dashboard/dashboard-actions';
import * as dashboardSelectors from '../dashboard/dashboard-selectors';
import * as dataSelectors from '../data/data-selectors';
import useRentalscapeConfig from '../hooks/use-rentalscape-config';
import { VerifiedPropertyIcon } from '../license/components/license-content-styles';
import { HistoricPaymentPeriods } from './modules/historic-payment-periods';
import { LicenseDropdown } from './modules/license-dropdown';
import { LoadingView } from './modules/loading-view';
import { TimeFrameDropdown } from './modules/time-frame-dropdown';
import { StreetAddress, TaxReportingViewDiv } from './styles';
import { License, TaxTabFeatureConfig } from './types';

export function PropertyTaxReportingView(): React.ReactElement {
  const dispatch = useDispatch();
  const {
    currentProperty: propertyData,
    loading: propertyDataLoading,
    goToPropertyDetails,
  } = usePropertyDetails();

  const { propertyDeckardId } = usePropertyDeckardId();

  const propertyOverviewTaxTab: TaxTabFeatureConfig = useSelector(
    dataSelectors.betaFeatureConfigValue(BETA_FEATURES.propertyOverviewTaxTab)
  );

  /** This is an Immutable Record. */
  const permitContextRecord = useSelector(dashboardSelectors.permitContext);

  const rentalscapeConfig: RentalscapeConfig = useRentalscapeConfig();

  const taxModule = propertyOverviewTaxTab?.expressCustomer
    ? rentalscapeConfig?.expressSTRTaxModule
    : rentalscapeConfig?.strTaxModule;

  // Co-erce the value as GQL defaults the field and its sub-fields as optional
  const taxConfiguration = (taxModule?.tax_config ?? []) as TaxConfiguration;

  const licenseDataWithPayments: License[] | string = useSelector(
    dataSelectors.totPaymentsData
  );

  const [selectedLicenseId, setSelectedLicenseId] = useState<
    number | undefined
  >(undefined);

  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());

  const handleAutoSelectByAppContext = useCallback(
    (targetLicenseNumber: string | null) => {
      /** Don't want to processes against a string as we're going to be targeting a license via the license number. */
      if (!Array.isArray(licenseDataWithPayments)) return;

      const possibleMatch = licenseDataWithPayments.find(
        (e) => e.certificateInfo.licenseNumber === targetLicenseNumber
      );
      /** Only auto-fill when there is a match. TypeScript does not properly infer Object.is here. */
      if (!possibleMatch) return;

      setSelectedLicenseId(possibleMatch.certificateInfo.licenseId);
      dispatch(dashboardActions.clearPermitContextRecord());
    },
    [dispatch, licenseDataWithPayments]
  );

  useEffect(() => {
    /**
     * The primary purpose of this is to handle the automated selection of a permit when the URL contains a
     * valid set of search parameters for processing payments related to each license associated with the property.
     * E.g., opening the property associated with a permit payment, displayed in the Top Level TOT table, in a new tab.
     */

    const currentLocation = new URL(window.location.toString());

    const targetLicenseNumber = currentLocation.searchParams.get('permit');
    handleAutoSelectByAppContext(targetLicenseNumber);

    // Wait for the license data to be loaded before replacing the history.
    if (Array.isArray(licenseDataWithPayments)) {
      /** Always replace the history as we don't have a need for the details anymore. */
      window.history.replaceState(
        null,
        '',
        `${window.location.origin}${window.location.pathname}`
      );
    }
  }, [handleAutoSelectByAppContext, licenseDataWithPayments]);

  useEffect(() => {
    /**
     * The primary purpose of this is to handle the automated selection of a permit when there is
     * a valid context within the Redux store to act upon.
     * E.g., opening the property associated with a permit payment, displayed in the Top Level TOT table, in the same tab.
     */

    const permitContext = permitContextRecord.toObject();
    /** We are only concerned with whether there is a license number to match against. */
    if (!permitContext.licenseNumber) return;

    handleAutoSelectByAppContext(permitContext.licenseNumber);

    return () => {
      dispatch(dashboardActions.clearPermitContextRecord());
    };
  }, [dispatch, handleAutoSelectByAppContext, permitContextRecord]);

  useEffect(() => {
    if (!propertyOverviewTaxTab?.enabled) {
      goToPropertyDetails({
        propertyDeckardId: propertyDeckardId,
        propertyDetailsTab: PROPERTY_DETAILS_TAB_NAMES.overview,
      });
    }
  }, [goToPropertyDetails, propertyDeckardId, propertyOverviewTaxTab]);

  if (
    propertyDataLoading ||
    typeof licenseDataWithPayments === 'string' ||
    licenseDataWithPayments === undefined
  ) {
    return <LoadingView isLoadingOrErrored={licenseDataWithPayments} />;
  }

  const permitPaymentDataIsEmpty = isEmpty(licenseDataWithPayments);
  const displayHistoricPaymentPeriods =
    propertyOverviewTaxTab?.enabled &&
    !permitPaymentDataIsEmpty &&
    selectedLicenseId !== undefined;

  return (
    <TaxReportingViewDiv data-testid="property-details-tax-view">
      <StreetAddress data-testid="property-tax-reporting-tab-header-address">
        {propertyData.address}
        <VerifiedPropertyIcon
          verifiedStatus={String(propertyData?.verifiedStatus)}
        />
      </StreetAddress>

      <div className="flex gap-4">
        <LicenseDropdown
          disabled={permitPaymentDataIsEmpty}
          paymentsData={licenseDataWithPayments}
          selected={selectedLicenseId}
          onSelect={setSelectedLicenseId}
        />

        <TimeFrameDropdown
          disabled={permitPaymentDataIsEmpty}
          selected={selectedYear}
          onSelect={setSelectedYear}
        />
      </div>

      {displayHistoricPaymentPeriods && (
        <HistoricPaymentPeriods
          paymentsData={licenseDataWithPayments}
          providers={propertyOverviewTaxTab.providers}
          reportingDetails={propertyOverviewTaxTab.reportingDetails}
          selectedLicenseId={selectedLicenseId}
          selectedYear={selectedYear}
          taxConfiguration={taxConfiguration}
        />
      )}
    </TaxReportingViewDiv>
  );
}
